/**
 * A horizontal scrollbar at the TOP of the board as well as the bottom.
 *
 * The board is fifteen months wide inside a box a screen tall, so the bar at
 * the bottom is out of reach exactly when you need it: reading the Q1 outlook
 * on a row near the top means scrolling right, and the only control for that is
 * a screen below. A second bar above the header solves it, and the two are one
 * position — dragging either moves the board.
 *
 * The top strip is a real scroll container with nothing in it but a spacer of
 * the board's own width. That is the whole trick: a browser renders a scrollbar
 * for overflow it can measure, and the spacer is the measurement.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export function ScrollSync({
  width,
  className = '',
  children,
}: {
  /** The board's full width — what the top strip pretends to contain. */
  width: number;
  className?: string;
  children: React.ReactNode;
}) {
  const top = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  /**
   * Which element the pointer is driving. Without it the two `onScroll`
   * handlers set each other in a loop: the body's scroll moves the strip, whose
   * scroll moves the body, and a drag fights itself into a stutter.
   */
  const driver = useRef<'top' | 'body' | null>(null);
  const [overflows, setOverflows] = useState(false);

  const sync = useCallback((from: 'top' | 'body') => {
    if (driver.current && driver.current !== from) return;
    driver.current = from;
    const a = from === 'top' ? top.current : body.current;
    const b = from === 'top' ? body.current : top.current;
    if (a && b) {
      /*
        BY RATIO, not by raw offset. The body scrolls vertically too, so a
        classic scrollbar takes width off it — its maximum scrollLeft is then
        SMALLER than the strip's, and copying the number left the board short of
        its own end while the top bar sat against the stop.
      */
      const maxA = a.scrollWidth - a.clientWidth;
      const maxB = b.scrollWidth - b.clientWidth;
      const next = maxA > 0 ? Math.round((a.scrollLeft / maxA) * maxB) : 0;
      if (b.scrollLeft !== next) b.scrollLeft = next;
    }
    // Released on the next frame, so a continuous drag stays with one driver
    // and a fresh gesture on the other element can take over.
    requestAnimationFrame(() => {
      driver.current = null;
    });
  }, []);

  /**
   * Only render the top bar when there is something to scroll. On a wide screen
   * with a short board it would otherwise be a permanent grey strip above the
   * header that does nothing — a control is worse than no control when it has
   * no effect.
   */
  useEffect(() => {
    const el = body.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);

  return (
    <div className={className}>
      {overflows && (
        <div
          ref={top}
          onScroll={() => sync('top')}
          aria-hidden
          className="always-scrollbar overflow-x-auto overflow-y-hidden border-b border-gray-200"
        >
          <div style={{ width, height: 1 }} />
        </div>
      )}
      <div
        ref={body}
        onScroll={() => sync('body')}
        className="always-scrollbar max-h-[70vh] overflow-auto"
      >
        {children}
      </div>
    </div>
  );
}
